import { DateTime } from "luxon";
import { z } from "zod";

export const dateRangeSchema = z
  .strictObject({ startDate: z.iso.date(), endDate: z.iso.date() })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "End date precedes start date",
    path: ["endDate"],
  });
export type DateRange = z.infer<typeof dateRangeSchema>;

/** Cumulative source-proven coverage under the unchanged query's day/time/target constraints. */
export const availabilitySearchCoverageSchema = z.strictObject({
  completedDateRanges: z.array(dateRangeSchema).max(256),
  // Pending work is not proof of coverage. Absent when the source cannot continue safely.
  nextDateRange: dateRangeSchema.nullable(),
});
export type AvailabilitySearchCoverage = z.infer<typeof availabilitySearchCoverageSchema>;

/** Merge inclusive calendar ranges without filling unsearched gaps between them. */
export function mergeSearchDateRanges(ranges: readonly DateRange[]): DateRange[] {
  const merged: DateRange[] = [];

  for (const range of ranges.toSorted((a, b) => a.startDate.localeCompare(b.startDate))) {
    const prior = merged.at(-1);

    if (prior && range.startDate <= DateTime.fromISO(prior.endDate, { zone: "utc" }).plus({ days: 1 }).toISODate()!) {
      prior.endDate = prior.endDate > range.endDate ? prior.endDate : range.endDate;
    } else {
      merged.push({ ...range });
    }
  }

  return merged;
}

/** Every provider tier/member/read must prove coverage before a combined search can claim it. */
export function intersectSearchDateRanges(rangeSets: readonly (readonly DateRange[])[]): DateRange[] {
  if (rangeSets.length === 0) return [];

  return rangeSets.slice(1).reduce<DateRange[]>((intersection, ranges) => {
    const overlaps = intersection.flatMap((left) =>
      ranges.flatMap((right) => {
        const startDate = left.startDate > right.startDate ? left.startDate : right.startDate;
        const endDate = left.endDate < right.endDate ? left.endDate : right.endDate;

        return startDate <= endDate ? [{ startDate, endDate }] : [];
      })
    );

    return mergeSearchDateRanges(overlaps);
  }, mergeSearchDateRanges(rangeSets[0]!));
}
